import type { SyncRecord } from './sync'

/**
 * Thin client for the sync API.
 *
 * Every call resolves rather than throws, because sync runs in the background
 * of a working offline app — an unreachable server is an ordinary condition
 * here, not an exception, and it must never surface as an unhandled rejection
 * mid-drill.
 */

export interface ApiFailure {
  ok: false
  code: string
  error: string
  /** True when the request never reached the server, so it's worth retrying. */
  offline: boolean
}

export type ApiResult<T> = ({ ok: true } & T) | ApiFailure

export interface ServerStatus {
  configured: boolean
  /** Whether any account exists yet — a count, never a list of who. */
  hasAccounts: boolean
  signedIn: boolean
  device: { id: string; name: string } | null
  isAdmin: boolean
  /** Languages this account may use. Drives which pills appear. */
  languages: string[]
  /** Whether this particular language site is one of them. */
  entitledHere: boolean
  /** Whether a free trial of this language is still available. */
  trialAvailable: boolean
  /**
   * How this site's language is held — not merely whether it is.
   *
   * `entitledHere` cannot tell a trial apart from a paid subscription, and that
   * is the difference Settings needs in order to offer checkout to somebody
   * still inside their free week.
   */
  standing: { source: string; expiresAt: number | null } | null
}

export interface AdminUser {
  id: string
  languages: string[]
  email: string
  name: string
  disabled: boolean
  isAdmin: boolean
  /** Perpetual free access to every language, present and future. */
  comped: boolean
  createdAt: number
  records: number
  devices: number
  lastSeenAt: number | null
}

export type UserAction =
  | 'comp'
  | 'uncomp'
  | 'reset-password'
  | 'disable'
  | 'enable'
  | 'grant-admin'
  | 'revoke-admin'
  | 'sign-out-devices'


export interface DeviceRow {
  id: string
  name: string
  createdAt: number
  lastSeenAt: number | null
  isCurrent: boolean
}

async function call<T>(
  path: string,
  init: RequestInit = {},
  token?: string | null,
): Promise<ApiResult<T>> {
  try {
    const response = await fetch(path, {
      ...init,
      headers: {
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init.headers ?? {}),
      },
    })

    const text = await response.text()
    let body: unknown = null
    try {
      body = text ? JSON.parse(text) : null
    } catch {
      // A non-JSON body means something in front of the app answered — most
      // often an HTML error page. Say so rather than showing a parse error.
      return {
        ok: false,
        code: 'bad_response',
        error: `The server returned ${response.status} instead of data.`,
        offline: false,
      }
    }

    if (!response.ok) {
      const err = body as Partial<ApiFailure>
      return {
        ok: false,
        code: err?.code ?? 'http_error',
        error: err?.error ?? `Request failed (${response.status}).`,
        offline: false,
      }
    }

    return body as { ok: true } & T
  } catch {
    return {
      ok: false,
      code: 'offline',
      error: 'No connection — your work is saved on this device and will sync later.',
      offline: true,
    }
  }
}

export function getStatus(token?: string | null): Promise<ApiResult<ServerStatus>> {
  return call<ServerStatus>('/api/status', { method: 'GET' }, token)
}

export interface DeviceGrant {
  token: string
  deviceId: string
  deviceName: string
  user: { email: string; name: string }
}

export function login(
  email: string,
  password: string,
  deviceName: string,
): Promise<ApiResult<DeviceGrant>> {
  return call('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password, deviceName }),
  })
}

export function pull(
  since: number,
  token: string,
): Promise<ApiResult<{ records: SyncRecord[]; cursor: number; hasMore: boolean }>> {
  return call(`/api/sync?since=${encodeURIComponent(String(since))}`, { method: 'GET' }, token)
}

export function push(
  records: SyncRecord[],
  token: string,
): Promise<ApiResult<{ cursor: number; applied: number; skipped: number }>> {
  return call('/api/sync', { method: 'POST', body: JSON.stringify({ records }) }, token)
}

export function listDevices(token: string): Promise<ApiResult<{ devices: DeviceRow[] }>> {
  return call('/api/devices', { method: 'GET' }, token)
}

export function revokeDevice(deviceId: string, token: string): Promise<ApiResult<object>> {
  return call('/api/devices', { method: 'POST', body: JSON.stringify({ deviceId }) }, token)
}

/** A readable default device name, since the user should not have to invent one. */
export function guessDeviceName(): string {
  if (typeof navigator === 'undefined') return 'Device'
  const ua = navigator.userAgent
  if (/android/i.test(ua)) return 'Android phone'
  if (/iphone/i.test(ua)) return 'iPhone'
  if (/ipad/i.test(ua)) return 'iPad'
  if (/mac os x/i.test(ua)) return 'Mac'
  if (/windows/i.test(ua)) return 'Windows PC'
  return 'Browser'
}

export function listAccounts(
  token: string,
): Promise<ApiResult<{ users: AdminUser[]; callerId: string | null }>> {
  return call('/api/admin/users', { method: 'GET' }, token)
}

export function createAccount(
  token: string,
  email: string,
  name: string,
  isAdmin: boolean,
  /** Free access to every language, present and future, from the moment they exist. */
  comped = false,
): Promise<ApiResult<{ user: AdminUser; password: string }>> {
  return call(
    '/api/admin/users',
    { method: 'POST', body: JSON.stringify({ email, name, isAdmin, comped }) },
    token,
  )
}

export function actOnAccount(
  token: string,
  userId: string,
  action: UserAction,
): Promise<ApiResult<{ password?: string; revoked?: number }>> {
  return call(
    `/api/admin/users/${encodeURIComponent(userId)}`,
    { method: 'POST', body: JSON.stringify({ action }) },
    token,
  )
}

export function deleteAccount(token: string, userId: string): Promise<ApiResult<object>> {
  return call(`/api/admin/users/${encodeURIComponent(userId)}`, { method: 'DELETE' }, token)
}

export interface InviteRow {
  id: string
  email: string
  name: string
  isAdmin: boolean
  createdAt: number
  expiresAt: number
  acceptedAt: number | null
  revoked: boolean
  live: boolean
}

export function listInvites(token: string): Promise<ApiResult<{ invites: InviteRow[] }>> {
  return call('/api/admin/invites', { method: 'GET' }, token)
}

export function createInvite(
  token: string,
  email: string,
  name: string,
  isAdmin: boolean,
): Promise<ApiResult<{ invite: InviteRow; url: string; emailed?: boolean; emailError?: string }>> {
  return call(
    '/api/admin/invites',
    { method: 'POST', body: JSON.stringify({ email, name, isAdmin }) },
    token,
  )
}

export function revokeInvite(token: string, id: string): Promise<ApiResult<object>> {
  return call(`/api/admin/invites/${encodeURIComponent(id)}`, { method: 'DELETE' }, token)
}

/** Public: what an invitation link is for, before anyone types a password. */
export function checkInvite(
  inviteToken: string,
): Promise<ApiResult<{ email: string; name: string; expiresAt: number }>> {
  return call(`/api/invite?token=${encodeURIComponent(inviteToken)}`, { method: 'GET' })
}

/** Public: redeem it. Creates the account and signs this device in. */
export function acceptInvite(
  inviteToken: string,
  password: string,
  deviceName: string,
): Promise<ApiResult<DeviceGrant>> {
  return call('/api/invite', {
    method: 'POST',
    body: JSON.stringify({ token: inviteToken, password, deviceName }),
  })
}

export function grantLanguage(
  token: string,
  userId: string,
  language: string,
): Promise<ApiResult<object>> {
  return call(
    `/api/admin/users/${encodeURIComponent(userId)}`,
    { method: 'POST', body: JSON.stringify({ action: 'grant-language', language }) },
    token,
  )
}

export function revokeLanguageAccess(
  token: string,
  userId: string,
  language: string,
): Promise<ApiResult<object>> {
  return call(
    `/api/admin/users/${encodeURIComponent(userId)}`,
    { method: 'POST', body: JSON.stringify({ action: 'revoke-language', language }) },
    token,
  )
}

export function signUp(
  email: string,
  password: string,
  deviceName: string,
): Promise<ApiResult<DeviceGrant & { trial: { language: string; expiresAt: number; days: number } }>> {
  return call('/api/auth/signup', {
    method: 'POST',
    body: JSON.stringify({ email, password, deviceName }),
  })
}

export function startTrial(
  token: string,
): Promise<ApiResult<{ language: string; expiresAt: number; days: number }>> {
  return call('/api/trial', { method: 'POST' }, token)
}

export interface Allowance {
  used: number
  limit: number
  remaining: number
}

/**
 * Ask Claude for a draft island or scenario.
 *
 * Deliberately not `call()`'s usual fire-and-forget: this one costs the account
 * a generation from a daily allowance, so the caller needs the allowance back
 * on both paths — including the failures, where knowing whether it was charged
 * is the difference between "try again" and "wait until tomorrow".
 */
export function generateContent(
  token: string,
  body: {
    kind: 'island' | 'scenario'
    brief: string
    register: string
    count: number
    /** Where the learner is, 1-6, so the draft is pitched at them. */
    level: number
    /** Only for scenarios: the islands the beats may point at. */
    islands?: unknown[]
  },
): Promise<ApiResult<{ kind: string; language: string; draft: unknown; attempts: number; allowance: Allowance }>> {
  return call('/api/generate', { method: 'POST', body: JSON.stringify(body) }, token)
}

/**
 * The learner's own AI keys: status only — a key itself never comes back.
 *
 * One credential per provider, so somebody who already pays OpenAI is not asked
 * to open an Anthropic account to use a feature they are funding themselves.
 */
export interface AiCredential {
  provider: string
  /** The model this key will be used with. */
  model: string
  /** Last four characters, so the UI can say which key without holding one. */
  hint: string
  status: 'pending' | 'valid' | 'invalid'
  note: string | null
  checkedAt: number | null
}

/** Everything the screen needs to draw a field, sent by the server. */
export interface AiProviderInfo {
  provider: string
  label: string
  /** Where to go and get one — otherwise this sends people off to search. */
  keysUrl: string
  placeholder: string
  defaultModel: string
}

export interface AiCredentialState {
  /** False when the site has no sealing secret, so a key cannot be stored. */
  storable: boolean
  /** Whether generation still works for someone who brings no key. */
  platformKey: boolean
  /*
   * The provider list comes from the server rather than being written out
   * here. The dispatcher decides which providers exist; a fourth one should
   * appear in settings without touching the client.
   */
  providers: AiProviderInfo[]
  /** Only the providers this learner has actually set up. */
  credentials: AiCredential[]
}

export function getAiCredentials(token: string): Promise<ApiResult<AiCredentialState>> {
  return call('/api/ai-credentials', {}, token)
}

const at = (provider: string) => `/api/ai-credentials?provider=${encodeURIComponent(provider)}`

export function putAiCredential(
  token: string,
  provider: string,
  key: string,
  model?: string,
): Promise<ApiResult<{ credential: AiCredential }>> {
  return call(at(provider), { method: 'PUT', body: JSON.stringify({ key, model }) }, token)
}

/** Re-check a stored key, for a learner whose credit has run out since. */
export function recheckAiCredential(
  token: string,
  provider: string,
): Promise<ApiResult<{ credential: AiCredential }>> {
  return call(at(provider), { method: 'POST' }, token)
}

export function deleteAiCredential(
  token: string,
  provider: string,
): Promise<ApiResult<{ credentials: AiCredential[] }>> {
  return call(at(provider), { method: 'DELETE' }, token)
}

/** What is left today, and whether the site has a key at all. */
export function generationStatus(
  token: string,
): Promise<ApiResult<{ configured: boolean; allowance: Allowance }>> {
  return call('/api/generate', {}, token)
}

/**
 * Begin checkout for the language of the current site.
 *
 * Nothing about what is being bought is sent: the server takes the language
 * from the hostname and the price from its own configuration, so there is no
 * field here that could be edited into a cheaper subscription.
 */
export function startCheckout(token: string): Promise<ApiResult<{ url: string }>> {
  return call('/api/billing/checkout', { method: 'POST' }, token)
}

/** Stripe's own page for changing a card, seeing invoices, or cancelling. */
export function openBillingPortal(token: string): Promise<ApiResult<{ url: string }>> {
  return call('/api/billing/portal', { method: 'POST' }, token)
}

/**
 * Ask for a password reset link.
 *
 * Always resolves the same way whether or not the address has an account — the
 * server refuses to say, and the UI must not imply otherwise.
 */
export function requestReset(email: string): Promise<ApiResult<{ message: string }>> {
  return call('/api/auth/reset', { method: 'POST', body: JSON.stringify({ email }) })
}

/** Is a reset link still good? PATCH, so the token stays out of the URL. */
export function checkReset(token: string): Promise<ApiResult<{ email?: string }>> {
  return call('/api/auth/reset', { method: 'PATCH', body: JSON.stringify({ token }) })
}

/** Spend the link and set the new password. */
export function completeReset(token: string, password: string): Promise<ApiResult<object>> {
  return call('/api/auth/reset', { method: 'PUT', body: JSON.stringify({ token, password }) })
}
